import { useState, useRef } from 'react';
import { Download, Upload, Plus, Trash2, FileJson, KeyRound, Check, Eye, EyeOff } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { getAdminToken, setAdminToken } from '../../lib/adminToken';

// ── Admin Token 設置子組件 ──
function AdminTokenSettings() {
  const [token, setToken] = useState(getAdminToken());
  const [showToken, setShowToken] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setAdminToken(token);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type={showToken ? 'text' : 'password'}
            placeholder="輸入 Worker Admin Token..."
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="w-full px-3 py-2 pr-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
          />
          <button
            type="button"
            onClick={() => setShowToken(!showToken)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <button
          type="button"
          onClick={handleSave}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-indigo-500 text-white hover:bg-indigo-600 transition-colors"
        >
          {saved ? <Check size={14} /> : <KeyRound size={14} />}
          {saved ? '已保存' : '保存'}
        </button>
      </div>
      <p className="text-xs text-slate-500">
        設置後可使用「案例庫」與「生成預覽連結」功能。Token 保存在瀏覽器本地。
      </p>
    </div>
  );
}

export function Settings() {
  const projects = useProjectStore((state) => state.projects);
  const currentProjectId = useProjectStore((state) => state.currentProjectId);
  const loadProject = useProjectStore((state) => state.loadProject);
  const createProject = useProjectStore((state) => state.createProject);
  const deleteProject = useProjectStore((state) => state.deleteProject);
  const exportToJSON = useProjectStore((state) => state.exportToJSON);
  const importFromJSON = useProjectStore((state) => state.importFromJSON);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExportJSON = () => {
    const json = exportToJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `promptforge-${currentProjectId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportJSON = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        importFromJSON(reader.result as string);
        setImportError(null);
      } catch (err) {
        setImportError(err instanceof Error ? err.message : '導入失敗');
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="max-w-3xl mx-auto pb-10">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">設置</h2>

      <div className="mb-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">多項目管理</h3>
        {projects.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">沒有項目</p>
        ) : (
          <ul className="space-y-2">
            {projects.map((project) => (
              <li
                key={project.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  project.id === currentProjectId
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-slate-200 dark:border-slate-700'
                }`}
              >
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{project.displayName}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{project.name}</p>
                </div>
                <div className="flex items-center gap-2">
                  {project.id !== currentProjectId && (
                    <button
                      type="button"
                      onClick={() => loadProject(project.id)}
                      className="px-3 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      載入
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => deleteProject(project.id)}
                    disabled={projects.length === 1}
                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-30"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={createProject}
          className="mt-4 flex items-center gap-1.5 px-4 py-2 text-sm rounded text-white"
          style={{ background: 'var(--pf-primary, #3b82f6)' }}
        >
          <Plus size={14} /> 新增項目
        </button>
      </div>

      <div className="mb-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">API 設置</h3>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
          設置 Worker Admin Token 以使用案例庫、預覽連結等進階功能。
        </p>
        <AdminTokenSettings />
      </div>

      <div className="mb-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">導入 / 導出</h3>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
          將當前項目配置導出為 JSON，或在其他設備上導入繼續編輯。
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleExportJSON}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded bg-slate-900 hover:bg-slate-800 text-white transition-colors"
          >
            <Download size={14} /> 導出 JSON
          </button>
          <label className="flex items-center gap-1.5 px-4 py-2 text-sm rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors">
            <Upload size={14} /> 導入 JSON
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={handleImportJSON}
            />
          </label>
        </div>
        {importError && (
          <p className="mt-3 text-sm text-red-500 flex items-center gap-1">
            <FileJson size={14} /> {importError}
          </p>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">關於 PromptForge</h3>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
          PromptForge 是一個基於「分層一次性提示詞」的網站生成配置器。
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          版本：v1.0 · 技術棧：React 18 + TypeScript + Vite + Tailwind CSS + Zustand
        </p>
      </div>
    </div>
  );
}
